/**
 * Act II operations (alpha): 15-minute shifts with a running ledger, five-category Stars and a shift report.
 * All values are integers so they persist exactly in the terminal record's optional `ops` block.
 */
import { tierIndex, AIRPORTS } from './airports.js';
import type { EconomyData } from './types.js';
import { CREW } from './config.js';
import { readAirline, freshAirline } from './contracts.js';
import { tier } from './airports.js';

/** Off-floor training for one skill pip takes two minutes (MGT-003). */
export const TRAINING_FRAMES = 7200;
/** Staff rest beside the crew room (MGT-004); its placement sets break travel time. */
/** Beside the pilot office, wherever the current airport's map puts it. */
export const STAFF_ROOM = { get x() { return CREW.x + 70; }, get y() { return CREW.y; } };
/** Energy: working drains it (full in about 8 minutes of steady work, faster in a rush); breaks start below BREAK_AT. */
export const ENERGY_DRAIN = 100 / 28800, RUSH_DRAIN = 1.5, BREAK_AT = 25, ENERGY_RECOVER = 100 / 1800;
/** Captain rally aura (MGT-005): staff in the captain's department work 25% faster and tire half as fast. */
export const RALLY_RADIUS = 220, RALLY_SPEED = 1.25;
/** Desk work speed from skill (+25% per pip above 1, capped at +50%). */
export function skillSpeed(skill: number): number { return Math.min(1.5, 1 + 0.25 * (Math.max(1, skill) - 1)); }
/** An aircraft waits at most this long (2 airport minutes) for missing bags before leaving late (MGT-007). */
export const BAG_WAIT = 600;
/** Fast Track (MGT-008) needs two Security lanes and a 3★ airport. */
export const FAST_TRACK_STARS = 30;
export function fastTrackAvailable(o: Pick<OpsRecord, 'starsOverall'>, securityLanes: number): boolean { return securityLanes >= 2 && o.starsOverall >= FAST_TRACK_STARS; }
/** MGT-017 airport policies: each option states its actual tradeoff. */
export const SERVICE_POLICIES = [
    { name: 'Thrifty', wages: 0.8, speed: 0.85, text: 'wages −20%, staff work 15% slower' },
    { name: 'Standard', wages: 1, speed: 1, text: 'normal wages and speed' },
    { name: 'Premium', wages: 1.25, speed: 1.15, text: 'wages +25%, staff work 15% faster' },
] as const;
export const BOARDING_POLICIES = [
    { name: 'Wait for full', text: 'visiting aircraft hold to their boarding window: more passengers, more late departures' },
    { name: 'On schedule', text: 'visiting aircraft leave at the scheduled time: better On-time, fewer passengers' },
] as const;
export const BREAK_POLICIES = [
    { name: 'Late breaks', at: 25, text: 'staff work until tired (25%): fewer breaks, slower when tired' },
    { name: 'Early breaks', at: 40, text: 'staff rest at 40%: more time off the floor, fresher staff' },
] as const;
/** One shift is 15 real minutes. */
export const SHIFT_FRAMES = 54000;
/** Sampling cadence for queue, litter and comfort averages. */
export const SHIFT_SAMPLE_FRAMES = 60;
/** A departure counts as on time when it leaves within two airport minutes of schedule (300 frames each). */
export const ON_TIME_FRAMES = 600;

export const OPS_KEYS = [
    'shift', 'shiftFrames', 'shiftEarned', 'shiftRetail', 'shiftWalkouts', 'shiftServed',
    'shiftWaitSum', 'shiftWaitSamples', 'shiftLitterSum', 'shiftLitterSamples', 'shiftComfortSum', 'shiftComfortSamples',
    'shiftDepartures', 'shiftOnTime',
    'starsQueues', 'starsClean', 'starsComfort', 'starsShops', 'starsOnTime', 'starsOverall',
    'reportShift', 'reportIncome', 'reportRetail', 'reportWages', 'reportAdvance', 'reportDepartures', 'reportOnTime', 'reportWalkouts', 'reportStarsBefore',
    'unpaidShifts', 'holdBags', 'fastTrack', 'policyService', 'policyBoarding', 'policyBreak',
    'eventKind', 'eventAt', 'eventAnnounced', 'eventDone', 'crisis0', 'crisis1', 'crisis2', 'crisisAge0', 'crisisAge1', 'crisisAge2', 'crisisHold0', 'crisisHold1', 'crisisHold2', 'crisisGate0', 'crisisGate1', 'crisisGate2', 'crisesRolled', 'crisisTypes', 'crisisRewards', 'ferryClean', 'ferryBase', 'ferryFirst', 'ferryLast', 'ferryWalkouts',
    'goal0', 'goal1', 'goal2', 'goalTarget0', 'goalTarget1', 'goalTarget2', 'goalsMet', 'crisesFixed',
    'tier0', 'tier1', 'tier2', 'tier3', 'tier4', 'tier5', 'tier6', 'tier7', 'retailModule',
] as const;
export type OpsKey = (typeof OPS_KEYS)[number];
export type OpsRecord = Record<OpsKey, number>;
export const STAR_KEYS = ['starsQueues', 'starsClean', 'starsComfort', 'starsShops', 'starsOnTime'] as const;
export const STAR_LABELS: Record<(typeof STAR_KEYS)[number], string> = { starsQueues: 'Queues', starsClean: 'Clean', starsComfort: 'Comfort', starsShops: 'Shops & Food', starsOnTime: 'On-time' };
const LIMIT = 1e14;

export function freshOps(): OpsRecord {
    return { ...Object.fromEntries(OPS_KEYS.map(key => [key, 0])), shift: 1, policyService: 1 } as OpsRecord;
}
/**
 * Total validation for a stored ops block: only known keys, non-negative integers, stars in tenths 0 or 10–50.
 * Keys added by later Act II slices may be missing from older blocks and read as fresh values.
 */
export function validateOps(raw: unknown): OpsRecord | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const given = raw as Record<string, unknown>;
    if (Object.keys(given).some(key => !(OPS_KEYS as readonly string[]).includes(key))) return null;
    const r: Record<string, unknown> = { ...freshOps(), ...given };
    if (!OPS_KEYS.every(key => Number.isSafeInteger(r[key]) && (r[key] as number) >= 0 && (r[key] as number) <= LIMIT)) return null;
    const stars = [...STAR_KEYS, 'starsOverall', 'reportStarsBefore'] as const;
    if (stars.some(key => { const v = r[key] as number; return v !== 0 && (v < 10 || v > 50); })) return null;
    if ((r.shift as number) < 1 || (r.shiftFrames as number) > SHIFT_FRAMES || (r.policyService as number) > 2 || (r.policyBoarding as number) > 1 || (r.policyBreak as number) > 1 || (r.fastTrack as number) > 1 || (r.retailModule as number) > 2 || [0, 1, 2, 3, 4, 5, 6, 7].some(i => (r[`tier${i}`] as number) > 2)) return null;
    return Object.fromEntries(OPS_KEYS.map(key => [key, r[key]])) as OpsRecord;
}

/** Shifts, Stars and wages run from Act II on (after the Promotion airport). */
export function shiftsActive(e: Pick<EconomyData, 'run'>): boolean {
    return tierIndex(e) > AIRPORTS.findIndex(a => a.code === 'SMR');
}

const clamp = (v: number) => Math.max(10, Math.min(50, Math.round(v * 10)));
/**
 * Rate a finished shift in tenths of a star (10–50). Each category answers to one lever:
 * Queues (average wait and walkouts), Clean (loose litter), Comfort (lounge seats), Shops & Food (retail share), On-time.
 * Overall is the mean, capped at the weakest category + 1.5.
 */
export function rateShift(o: OpsRecord): Pick<OpsRecord, (typeof STAR_KEYS)[number] | 'starsOverall'> {
    const wait = o.shiftWaitSamples ? o.shiftWaitSum / o.shiftWaitSamples : 0;
    const walkoutRate = o.shiftWalkouts / Math.max(10, o.shiftServed + o.shiftWalkouts);
    const queues = clamp(5 - wait / 40 - walkoutRate * 4);
    const litter = o.shiftLitterSamples ? o.shiftLitterSum / o.shiftLitterSamples : 0;
    const clean = clamp(5 - litter / 2);
    const seated = o.shiftComfortSamples ? o.shiftComfortSum / o.shiftComfortSamples / 100 : 1;
    const comfort = clamp(1 + 4 * seated);
    const share = o.shiftEarned ? o.shiftRetail / o.shiftEarned : 0;
    const shops = clamp(1 + share * 16);
    const ontime = clamp(o.shiftDepartures ? 1 + 4 * o.shiftOnTime / o.shiftDepartures : 3);
    const values = [queues, clean, comfort, shops, ontime], mean = values.reduce((a, b) => a + b, 0) / values.length;
    const overall = Math.min(Math.round(mean), Math.min(...values) + 15);
    return { starsQueues: queues, starsClean: clean, starsComfort: comfort, starsShops: shops, starsOnTime: ontime, starsOverall: overall };
}
/** Start the next shift: keep Stars, the last report and the unpaid streak; clear the running ledger. */
export function nextShift(o: OpsRecord): OpsRecord {
    const kept = Object.fromEntries(OPS_KEYS.filter(k => k.startsWith('stars') || k.startsWith('report') || k.startsWith('policy') || ['unpaidShifts', 'holdBags', 'fastTrack', 'ferryClean', 'goalsMet', 'crisesFixed', 'retailModule'].includes(k) || /^tier\d$/.test(k)).map(k => [k, o[k]]));
    return { ...freshOps(), ...kept, shift: o.shift + 1 } as OpsRecord;
}
export function starText(tenths: number): string { return tenths ? (tenths / 10).toFixed(1) + '★' : 'not rated yet'; }

/** MGT-012 reviews: what passengers said about the last shift, led by its weakest category (agrees with the Stars). */
const REVIEWS: Record<(typeof STAR_KEYS)[number], [string, string]> = {
    starsQueues: ['"The lines were long and slow."', '"Barely waited at all!"'],
    starsClean: ['"Litter everywhere near the desks."', '"Spotless terminal."'],
    starsComfort: ['"Nowhere to sit at the gates."', '"Comfy seats while we waited."'],
    starsShops: ['"No time or place to shop."', '"Great shopping before the flight."'],
    starsOnTime: ['"Our flight left late."', '"Right on time!"'],
};
export function reviewLines(o: Pick<OpsRecord, (typeof STAR_KEYS)[number]>): string[] {
    if (!o.starsQueues) return [];
    const sorted = [...STAR_KEYS].sort((a, b) => o[a] - o[b]);
    const worst = sorted[0], best = sorted[sorted.length - 1];
    return [o[worst] < 35 ? REVIEWS[worst][0] : REVIEWS[worst][1], REVIEWS[best][1]];
}

/**
 * Act II mastery (MGT-023): leaving Bayview (Wing 1) and Metro (the Founding) depends on how the airport runs, never on
 * lifetime earnings. After about twice the expected time a consultant tip appears; after three times the targets ease
 * so nobody is ever stuck.
 */
export const MASTERY_TIP_SHIFTS = 6, MASTERY_EASE_SHIFTS = 9, MASTERY_WAIVE_SHIFTS = 12;
export interface MasteryFacts { code: string; shift: number; starsOverall: number; contracts: number; international: number; ferryClean: boolean; onTimePct: number }
/** Mastery checklist rows; masteryMissing is exactly the labels of the rows not yet done. */
export function masteryRows(f: MasteryFacts): { label: string; done: boolean; detail: string }[] {
    if (f.shift > MASTERY_WAIVE_SHIFTS) return [{ label: '1 airline contract', done: f.contracts >= 1, detail: `${f.contracts} running · the Board stepped in` }];
    const eased = f.shift > MASTERY_EASE_SHIFTS, rows: { label: string; done: boolean; detail: string }[] = [];
    const stars = (need: number) => rows.push({ label: `A shift rated ${(need / 10).toFixed(1)}★`, done: f.starsOverall >= need, detail: `last shift ${(f.starsOverall / 10).toFixed(1)}★` });
    const contracts = (need: number) => rows.push({ label: `${need} airline contract${need === 1 ? '' : 's'}`, done: f.contracts >= need, detail: `${f.contracts} running` });
    if (f.code === 'BVM') {
        stars(eased ? 25 : 30); contracts(eased ? 1 : 2);
        if (!eased) rows.push({ label: 'A ferry day with no walkouts', done: f.ferryClean, detail: f.ferryClean ? 'done' : 'when the ferry lands' });
    } else if (f.code === 'MTR') {
        stars(eased ? 35 : 40); contracts(eased ? 2 : 3);
        if (!eased) rows.push({ label: 'An international airline', done: f.international >= 1, detail: `${f.international} signed` });
        const onTime = eased ? 70 : 85;
        rows.push({ label: `${onTime}% of a shift's departures on time`, done: f.onTimePct >= onTime, detail: `last shift ${f.onTimePct}%` });
    }
    return rows;
}
export function masteryMissing(f: MasteryFacts): string[] { return masteryRows(f).filter(r => !r.done).map(r => r.label); }
export function masteryTip(f: MasteryFacts): string {
    if (f.shift <= MASTERY_TIP_SHIFTS) return '';
    return f.shift > MASTERY_WAIVE_SHIFTS ? 'The Board steps in: keep one airline contract running to move on.' : f.shift > MASTERY_EASE_SHIFTS ? 'The Board eased the targets: they are now within reach.'
        : 'Consultant tip: assign helpers to the weakest desk, keep litter down and sign Friendly contracts first.';
}

type ActTwoState = Pick<EconomyData, 'run'> & Pick<OpsRecord, 'shift' | 'starsOverall' | 'reportDepartures' | 'reportOnTime' | 'ferryClean'> & { airline: string };
/** Facts the Act II beats and mastery read from the live terminal state. */
export function masteryFacts(e: ActTwoState): MasteryFacts & { standsUpgraded: number } {
    const a = readAirline(e.airline, e), fresh = freshAirline(e);
    return { code: tier(e).code, shift: e.shift, starsOverall: e.starsOverall, contracts: a.contracts.length, international: a.contracts.filter(c => c.international).length,
        ferryClean: !!e.ferryClean, onTimePct: e.reportDepartures ? Math.round(e.reportOnTime * 100 / e.reportDepartures) : 0,
        standsUpgraded: Math.max(0, a.stands.reduce((n, s) => n + s, 0) - fresh.stands.reduce((n, s) => n + s, 0)) };
}

/**
 * MGT-024 generated shift goals: three per shift, chosen from the last shift (repeatable for the same ledger). Goals
 * 1-5 raise a Star category (ids match STAR_KEYS order), 6 caps walkouts, 7 beats last shift's earnings. Each has a
 * reachable target; a first shift with no report falls back to plain targets.
 */
export const GOAL_TEXT = ['', 'Queues', 'Clean', 'Comfort', 'Shops & Food', 'On-time', 'Walkouts', 'Earnings'] as const;
export function pickGoals(o: OpsRecord): { goal: number; target: number }[] {
    const ranked = STAR_KEYS.map((key, i) => ({ id: i + 1, value: o[key] || 30 })).sort((a, b) => a.value - b.value || a.id - b.id);
    const star = (g: { id: number; value: number }) => ({ goal: g.id, target: Math.min(50, Math.max(30, g.value + 5)) });
    const third = o.reportWalkouts > 0 ? { goal: 6, target: Math.floor(o.reportWalkouts / 2) } : { goal: 7, target: Math.max(500, Math.round(o.reportIncome * 1.1)) };
    return [star(ranked[0]), star(ranked[1]), third];
}
/** Whether a goal was met by a finished shift's ledger and its fresh Stars. */
export function goalMet(goal: number, target: number, ledger: OpsRecord, stars: Pick<OpsRecord, (typeof STAR_KEYS)[number]>): boolean {
    if (goal >= 1 && goal <= 5) return stars[STAR_KEYS[goal - 1]] >= target;
    if (goal === 6) return ledger.shiftWalkouts <= target;
    if (goal === 7) return ledger.shiftEarned >= target;
    return false;
}
export function goalLabel(goal: number, target: number): string {
    return goal >= 1 && goal <= 5 ? `${GOAL_TEXT[goal]} ${(target / 10).toFixed(1)}★ or better` : goal === 6 ? `At most ${target} walkouts` : goal === 7 ? `Earn ${target.toLocaleString('en-US')} bills` : '';
}
export const GOAL_BONUS_MIN = 100;

/**
 * MGT-006 build slots (alpha): departments refit from tier 1 to 3 (+20% service speed per tier), optional departments
 * can be demolished for a 50% refund and rebuilt, and the retail slot runs one of three modules with a real tradeoff.
 */
export const OPTIONAL_DEPARTMENTS = [3, 4, 5, 7];
export const RETAIL_MODULES = [
    { name: 'Duty Free', spend: 1, service: 1, text: 'standard shopping' },
    { name: 'Café', spend: 0.7, service: 0.7, text: 'quick snacks: faster service, smaller spend' },
    { name: 'Gift Shop', spend: 1.5, service: 1.4, text: 'big spend, slower service' },
] as const;
export function tierSpeed(tier: number): number { return 1 + 0.2 * Math.max(0, Math.min(2, tier)); }
export function refitCost(baseCost: number, tier: number, costMult: number): number { return tier >= 2 ? Infinity : Math.ceil(baseCost * 4 * costMult * (tier + 1)); }
